import React, {useEffect, Suspense, lazy} from 'react';
import PropTypes from 'prop-types';
import {Route, Routes, useLocation} from 'react-router-dom';

import {useMarketingAttributionConsumer} from '../../hook/appUrl';
import Loading from '../loading/Loading';

import NotFound from '../../page/notFound/NotFound';
import Home from '../../page/home/Home';
import Product from '../../page/product/Product';
import Search from '../../page/search/Search';
import Brand from '../../page/brand/Brand';
import Seller from '../../page/seller/Seller';
import Discover from '../../page/discover/Discover';
import BrandRanking from '../../page/brandRanking/BrandRanking';
import Download from '../../page/download/Download';
import Collection from '../../page/collection/Collection';
import Collections from '../../page/collections/Collections';
import Login from '../../page/auth/login/Login.jsx';
import SignUp from '../../page/auth/signUp/SignUp';
import FriendInvite from '../../page/friendInvite/FriendInvite';
import SellerCollection from '../../page/sellerCollection/SellerCollection';
import Sell from '../../page/sell/Sell';
import Fees from '../../page/fees/Fees';
import CollectionCortis from '../../page/collectionCortis/CollectionCortis';
import ErrorPage from '../../page/errorPage/ErrorPage';
import BrandRadar from '../../page/brandRadar/BrandRadar';
import RequireAuth from './RequireAuth';

// SSR에서 실제 페이지 콘텐츠를 렌더링하지 않는 라우트만 lazy 유지
const SignUpSns = lazy(() => import('../../page/auth/signUp/SignUpSns'));
const SocialAuthComplete = lazy(() => import('../../page/auth/socialAuthComplete/SocialAuthComplete'));
const MyPageWrapper = lazy(() => import('../../page/myPage/MyPageWrapper'));
const Settings = lazy(() => import('../../page/settings/Settings'));
const PurchasedWrapper = lazy(() => import('../../page/purchased/PurchasedWrapper'));
const OrderWrapper = lazy(() => import('../../page/order/OrderWrapper'));
const Upload = lazy(() => import('../../page/upload/Upload'));
const CheckoutWrapper = lazy(() => import('../../page/checkout/CheckoutWrapper'));
const RegisterCheckoutShipping = lazy(() => import('../../page/checkout/shipping/RegisterCheckoutShipping'));
const CheckoutComplete = lazy(() => import('../../page/checkout/complete/CheckoutComplete'));
const CheckoutFail = lazy(() => import('../../page/checkout/fail/CheckoutFail').then(m => ({ default: m.CheckoutFail })));

/**
 * Component with contains all the global routes.
 * @param hostname the webapp hostname
 * @param location a location object to be used for matching children elements instead of the current history location
 * @param userAgent the user agent
 * @return {*}
 * @constructor
 */
export default function AppRoutes({ hostname, userAgent }) {
    const { pathname } = useLocation();

    const withSuspense = (element) => (
        <Suspense fallback={<Loading />}>
            {element}
        </Suspense>
    );

    useEffect(() => {
        // scroll to top on root page change (only when pathname changes)
        window.scrollTo(0, 0);
    }, [pathname]);

    // consume marketing attribution from location parameters
    useMarketingAttributionConsumer();

    // build the UI elements
    return (
        <Routes>
            <Route path="/" element={<Home hostname={hostname} />} />
            <Route path="/brand/:brandName/:categoryName?" element={<Brand hostname={hostname}/>} />
            <Route path="/collection/:collectionId/*" element={<Collection hostname={hostname} userAgent={userAgent} />} />
            <Route path="/collections" element={<Collections hostname={hostname} />} />
            <Route path="/discover/:discoverTab?/:categoryName?" element={<Discover hostname={hostname} userAgent={userAgent} />} />
            <Route path="/download" element={<Download hostname={hostname}/>} />
            <Route path="/sell" element={<Sell hostname={hostname} userAgent={userAgent}/>} />
            <Route path="/sell/fees" element={<Fees hostname={hostname} userAgent={userAgent}/>} />
            <Route path="/brand-ranking" element={<BrandRanking hostname={hostname} userAgent={userAgent} />} />
            <Route path="/brand-radar/:brandName?" element={<BrandRadar hostname={hostname} userAgent={userAgent} />} />
            <Route path="/product/:productId/*" element={<Product hostname={hostname} userAgent={userAgent} />} />
            <Route path="/upload" element={withSuspense(<RequireAuth><Upload hostname={hostname} userAgent={userAgent} /></RequireAuth>)} />
            <Route path="/seller/:sellerId/:encodedTitle?/:sellerTab?" element={<Seller hostname={hostname} userAgent={userAgent} />} />
            <Route path="/search" element={<Search hostname={hostname}/>} />
            <Route path="/search/:keyword/:categoryName?" element={<Search hostname={hostname}/>} />
            <Route path="/order/purchased" element={<Download hostname={hostname}/>} />
            <Route path="/order/selling" element={<Download hostname={hostname}/>} />
            <Route path="/login" element={<Login />} />
            <Route path="/signUp" element={<SignUp />} />
            <Route path="/signUpSns" element={withSuspense(<SignUpSns />)} />
            <Route path="/auth/kakao/complete" element={withSuspense(<SocialAuthComplete socialType={'KAKAO'} />)} />
            <Route path="/auth/apple/complete" element={withSuspense(<SocialAuthComplete socialType={'APPLE'}/>)} />
            <Route path="/auth/facebook/complete" element={withSuspense(<SocialAuthComplete socialType={'FACEBOOK'}/>)} />
            <Route path="/my/:sellerTab?" element={withSuspense(<RequireAuth><MyPageWrapper hostname={hostname} userAgent={userAgent} /></RequireAuth>)} />
            <Route path="/my/purchased" element={withSuspense(<RequireAuth><PurchasedWrapper hostname={hostname} userAgent={userAgent} /></RequireAuth>)} />
            <Route path="/my/purchased/:orderId" element={withSuspense(<RequireAuth><OrderWrapper hostname={hostname} userAgent={userAgent} /></RequireAuth>)} />
            <Route path="/my/settings" element={withSuspense(<RequireAuth><Settings hostname={hostname} userAgent={userAgent} /></RequireAuth>)} />

            <Route path="/checkout/:productId" element={withSuspense(<CheckoutWrapper hostname={hostname}/>)} />
            <Route path="/checkout/shipping/:productId" element={withSuspense(<RegisterCheckoutShipping />)} />
            <Route path="/checkout/complete" element={withSuspense(<CheckoutComplete />)} />
            <Route path="/checkout/fail" element={withSuspense(<CheckoutFail />)} />
            <Route path="/event/join/:code" element={<FriendInvite hostname={hostname} />} />
            <Route path="/vintage-shops/:sellerCollectionId?/*" element={<SellerCollection hostname={hostname} />} />
            <Route path="/feature/cortis" element={<CollectionCortis />} />
            <Route path="/temporary-wrong" element={<ErrorPage />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}
AppRoutes.propTypes = {
    hostname: PropTypes.string.isRequired,
    location: PropTypes.object,
    userAgent: PropTypes.string.isRequired
};
